import { useEffect, useState } from 'react';

// Global cache of script load promises to guarantee idempotent loading across components/pages.
declare global {
  var __externalScriptPromises: Record<string, Promise<Event>> | undefined;
}

export type ExternalScriptStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface UseExternalScriptOptions {
  id?: string; // optional DOM id for the <script>
  async?: boolean;
  defer?: boolean;
  retryCount?: number; // basic retry attempts on network failure
  retryDelayMs?: number;
  onLoaded?: () => void;
  onError?: (error: any) => void;
  attributes?: Record<string, string>;
}

/**
 * useExternalScript
 * Safely injects an external script exactly once (per src) with load/error tracking & optional retry logic.
 * Multiple callers await the same underlying promise via a global cache.
 */
export const useExternalScript = (
  src: string | undefined,
  {
    id,
    async = true,
    defer = true,
    retryCount = 0,
    retryDelayMs = 1000,
    onLoaded,
    onError,
    attributes,
  }: UseExternalScriptOptions = {},
): ExternalScriptStatus => {
  const [status, setStatus] = useState<ExternalScriptStatus>('idle');

  useEffect(() => {
    if (!src || typeof window === 'undefined' || typeof document === 'undefined') return;

    if (!window.__externalScriptPromises) window.__externalScriptPromises = {};

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing && (existing as any).dataset.__loaded === 'true') {
      setStatus('loaded');
      onLoaded?.();
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const load = (): Promise<Event> => {
      attempts++;
      if (window.__externalScriptPromises![src] !== undefined) return window.__externalScriptPromises![src];

      setStatus('loading');
      window.__externalScriptPromises![src] = new Promise<Event>((resolve, reject) => {
        let script: HTMLScriptElement;
        if (existing) {
          script = existing as HTMLScriptElement;
        } else {
          script = document.createElement('script');
          script.src = src;
          if (id) script.id = id;
          script.async = async;
          script.defer = defer;
          if (attributes) {
            Object.entries(attributes).forEach(([k, v]) => script.setAttribute(k, v));
          }
          script.dataset.__loaded = 'pending';
          document.head.appendChild(script);
        }
        script.addEventListener('load', (e) => {
          script.dataset.__loaded = 'true';
          resolve(e);
        });
        script.addEventListener('error', (e) => {
          script.dataset.__loaded = 'error';
          reject(e);
        });
      });
      return window.__externalScriptPromises![src];
    };

    const exec = async () => {
      try {
        await load();
        if (cancelled) return;
        setStatus('loaded');
        onLoaded?.();
      } catch (err) {
        if (cancelled) return;
        if (attempts <= retryCount) {
          await new Promise((r) => setTimeout(r, retryDelayMs));
          // Clear cache to allow retry
          delete window.__externalScriptPromises![src];
          exec();
          return;
        }
        setStatus('error');
        onError?.(err);
      }
    };
    exec();

    return () => {
      cancelled = true;
    };
  }, [src, id, async, defer, retryCount, retryDelayMs, onLoaded, onError, attributes]);

  return status;
};
